/**
 * @module features/visual/visualSearch
 *
 * Pure mapping between the `/visual` URL search params and the
 * `GET /visual/checkpoints` + `GET /visual/summary` query strings.
 */
import { VISUAL_CHECKPOINT_STATUSES, type VisualCheckpointStatus } from '@chatsdet/types';

export const VISUAL_SOURCES = ['native', 'playwright'] as const;
export const DEFAULT_VISUAL_STATUS: VisualCheckpointStatus = 'changed';

/** URL search state of `/visual`. `execution` / `run` are set by the run-page badges. */
export type VisualSearch = {
  status: VisualCheckpointStatus;
  suite?: string;
  workflow?: string;
  spec?: string;
  environment?: string;
  source?: (typeof VISUAL_SOURCES)[number];
  execution?: string;
  run?: string;
};

/** True when any filter beyond the default status narrows the list (including a run-page scope). */
export function isVisualSearchFiltered(search: VisualSearch): boolean {
  return (
    search.status !== DEFAULT_VISUAL_STATUS ||
    Boolean(
      search.suite ||
        search.workflow ||
        search.spec ||
        search.environment ||
        search.source ||
        search.execution ||
        search.run
    )
  );
}

/** What a run page badge counts: one native execution or one external run. */
export type VisualScope = { executionId: string } | { externalTestRunId: string };

export function parseVisualStatus(value: string | undefined): VisualCheckpointStatus {
  return VISUAL_CHECKPOINT_STATUSES.find((status) => status === value) ?? DEFAULT_VISUAL_STATUS;
}

export function parseVisualSource(value: string | undefined): VisualSearch['source'] {
  return VISUAL_SOURCES.find((source) => source === value);
}

/** Builds the list query string (without cursor/limit) from the URL state. */
export function visualSearchToQuery(search: VisualSearch): URLSearchParams {
  const params = new URLSearchParams();
  params.set('status', search.status);
  const entries: Array<[string, string | undefined]> = [
    ['executionId', search.execution],
    ['externalTestRunId', search.run],
    ['suiteId', search.suite],
    ['workflowId', search.workflow],
    ['specFile', search.spec],
    ['environmentName', search.environment],
    ['source', search.source],
  ];
  for (const [key, value] of entries) {
    if (value) params.set(key, value);
  }
  return params;
}

export function visualScopeToQuery(scope: VisualScope): URLSearchParams {
  return new URLSearchParams(
    'executionId' in scope
      ? { executionId: scope.executionId }
      : { externalTestRunId: scope.externalTestRunId }
  );
}

/** Badge copy: "1 visual change" / "N visual changes". */
export function visualChangesBadgeText(changed: number): string {
  return `${changed} visual ${changed === 1 ? 'change' : 'changes'}`;
}

/** The `/visual` search a run-page badge links to. */
export function visualScopeToSearch(scope: VisualScope): VisualSearch {
  return 'executionId' in scope
    ? { status: 'changed', execution: scope.executionId }
    : { status: 'changed', run: scope.externalTestRunId };
}
